import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { ACCENT, cardStyle as makeCard } from '../../lib/uiTokens';
import Dropdown from '../../components/receptura/Dropdown';
import { MESICE_PLNE } from '../../lib/formatters';
import ObdobiNavigator from '../finance/ObdobiNavigator';
import PnlTabulka from './PnlTabulka';
import VysledovkaStatistikyPanel from './VysledovkaStatistikyPanel';
import VysledovkaVerdikt from './VysledovkaVerdikt';
import KDoreseni from './KDoreseni';
import KpiKarty from './KpiKarty';
import StrukturaPruh from './StrukturaPruh';

/**
 * Výsledovka · Přehled — P&L dashboard.
 *
 * **Dva režimy, ne tři (3.480.0).** „Dopočet k dnešku" byl výchozí a přitom to
 * bylo číslo, které není ani skutečnost, ani prognóza; vlnovka `~` u něj navíc
 * znamenala něco jiného než u projekce. Zůstalo:
 *   - **Zaúčtováno** — co je opravdu v dokladech a tržbách k dnešku
 *   - **Projekce do konce měsíce** — kam to tímhle tempem míří
 * Výchozí je Projekce u běžícího měsíce; u uzavřeného je projekce rovna
 * skutečnosti, takže tam přepínač nemá co říct a nekreslí se.
 *
 * Rozvržení desktopu: verdikt → P&L (EBITDA přilepená ke spodní hraně karty)
 * vlevo, statistiky a „K dořešení" vpravo (norma §1f — třetí sloupec není formulář).
 * Na mobilu se pořadí obrací podle toho, proč se modul otevírá: verdikt,
 * K dořešení, pak P&L v blocích, a grafika nakonec.
 *
 * Období řeší `ObdobiNavigator` — stejná komponenta jako DPH a AOP. Do 3.480.0
 * tu byl vlastní chip pruh, ze kterého navigátor kdysi vznikl, a obě kopie se
 * mezitím rozešly (uzávěrka jinde, jiné odsazení).
 */

const REZIMY = [
    { key: 'zauctovano', label: 'Zaúčtováno', labelKratky: 'Zaúčtováno' },
    { key: 'projekce',   label: 'Projekce do konce měsíce', labelKratky: 'Projekce měsíce' },
];

/**
 * Přepínač režimu. Aktivní pilulka je **neutrální plocha s akcentním rámečkem
 * a textem** — ne modrý závoj: tónované pozadí na takhle velkém ovladači táhlo
 * v tmavém motivu do modra.
 */
function RezimSelector({ rezim, onChange, isMobile }) {
    return (
        <div className={`flex ${isMobile ? 'w-full' : 'inline-flex'} items-center rounded-[9px]`}
            style={{ padding: 3, gap: 4, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}>
            {REZIMY.map(r => {
                const on = rezim === r.key;
                return (
                    <button key={r.key} onClick={() => onChange(r.key)}
                        className={`rounded-md transition-colors ${isMobile ? 'flex-1' : ''}`}
                        style={{
                            height: isMobile ? 44 : 28,
                            padding: isMobile ? 0 : '0 14px',
                            fontSize: isMobile ? 13 : 12,
                            fontWeight: on ? 700 : 600,
                            background: on ? 'var(--surface-elevated)' : 'transparent',
                            border: `1px solid ${on ? `${ACCENT}8c` : 'transparent'}`,
                            color: on ? ACCENT : 'var(--text-secondary)',
                        }}>
                        {isMobile ? r.labelKratky : r.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function PrehledTab({ isDark, user, isMobile = false, rezervaDole = null }) {
    const card = makeCard(isDark);
    const [ym, setYm] = useState(() => { const d = new Date(); return { rok: d.getFullYear(), mesic: d.getMonth() + 1 }; });
    const [variantaId, setVariantaId] = useState(null);
    const [rezim, setRezim] = useState('projekce');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [uzavirani, setUzavirani] = useState(false);

    const isCeo = user?.role === 'superadmin';
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const obdobi = `${ym.rok}-${String(ym.mesic).padStart(2, '0')}`;

    useEffect(() => { setVariantaId(null); }, [ym.rok]);

    useEffect(() => {
        setLoading(true);
        const vParam = variantaId ? `&varianta_id=${variantaId}` : '';
        fetch(`/api/finance/vysledovka/prehled?obdobi=${obdobi}${vParam}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { setData(d); if (d && variantaId == null) setVariantaId(d.varianta_id); setLoading(false); })
            .catch(() => setLoading(false));
    }, [obdobi, variantaId, refreshKey]);

    // V uzavřeném měsíci je projekce rovna skutečnosti — režim „Projekce" by
    // ukazoval totéž a jen mátl. Dosadí se Zaúčtováno a přepínač se nekreslí.
    const uzavreny = data?.pacing?.uzavreny;
    const aktivniRezim = uzavreny ? 'zauctovano' : rezim;

    // Uzávěrka měsíce (CEO) — zamkne editaci dokladů daného měsíce.
    const prepniUzavirku = async (cesta, otazka) => {
        if (!confirm(otazka)) return;
        setUzavirani(true);
        try {
            const r = await fetch(`/api/finance/mesic/${ym.rok}/${ym.mesic}/${cesta}`, {
                method: 'POST', headers: { 'X-CSRF-TOKEN': token },
            });
            if (r.ok) setRefreshKey(k => k + 1);
        } catch (e) { /* ignore */ }
        setUzavirani(false);
    };
    const mesicLabel = `${MESICE_PLNE[ym.mesic - 1]} ${ym.rok}`;

    if (loading && !data) {
        return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin" style={{ color: ACCENT }} /></div>;
    }
    if (!data) return <div className="text-center py-10" style={{ color: 'var(--text-tertiary)' }}>Nepodařilo se načíst data</div>;

    const p = data.pacing;
    const jeProj = aktivniRezim === 'projekce';

    const variantaDropdown = data.varianty?.length > 0 ? (
        <Dropdown value={variantaId ?? ''} onChange={(v) => setVariantaId(Number(v))}
            options={data.varianty.map(v => ({ value: v.id, label: `${v.nazev}${v.je_vychozi ? ' ★' : ''}` }))}
            isDark={isDark} accent={ACCENT} height={isMobile ? 44 : 32} minWidth={isMobile ? 0 : 150} />
    ) : null;

    const navigator = (
        <ObdobiNavigator
            mode="mesic" rok={ym.rok} mesic={ym.mesic}
            setRok={(r) => setYm(y => ({ ...y, rok: r }))}
            setMesic={(m) => setYm(y => ({ ...y, mesic: m }))}
            isAdmin={isCeo} stavMesice={uzavreny ? 'uzavreny' : 'otevreny'} uzavirani={uzavirani}
            onUzavrit={() => prepniUzavirku('uzavrit', `Opravdu uzavřít ${mesicLabel}?`)}
            onZnovuOtevrit={() => prepniUzavirku('znovu-otevrit', `Znovu otevřít ${mesicLabel}?`)}
            popis={p ? `den ${p.den} z ${p.dnu}` : null}
            isMobile={isMobile} mezera={false}
        >
            {isMobile ? (
                <div className="flex flex-col gap-2">
                    {!uzavreny && <RezimSelector rezim={aktivniRezim} onChange={setRezim} isMobile />}
                    {variantaDropdown}
                </div>
            ) : (
                <>
                    {!uzavreny && <RezimSelector rezim={aktivniRezim} onChange={setRezim} />}
                    {variantaDropdown}
                </>
            )}
        </ObdobiNavigator>
    );

    // Vysvětlivka režimů — jedna řádka u přepínače místo odstavce pod tabulkou.
    const vysvetlivka = uzavreny ? (
        <>Měsíc je uzavřený — tabulka ukazuje zaúčtovanou skutečnost celého měsíce.</>
    ) : isMobile ? (
        <>
            <b className="font-semibold" style={{ color: 'var(--text-primary)' }}>Zaúčtováno</b> = k {p?.den}. {ym.mesic}. ·{' '}
            <b className="font-semibold" style={{ color: 'var(--text-primary)' }}>Projekce</b> = kam měsíc tímhle tempem míří
        </>
    ) : (
        <>
            <b className="font-semibold" style={{ color: 'var(--text-primary)' }}>Zaúčtováno</b> = doklady a tržby
            k {p?.den}. {ym.mesic}. ·{' '}
            <b className="font-semibold" style={{ color: 'var(--text-primary)' }}>Projekce</b> = kam měsíc míří tímto
            tempem (mzdy z plánu směn, fixní náklady celoměsíčně)
        </>
    );

    // ── Mobil: odpověď první, tabulka pak ───────────────────────────────────
    //
    // Modul sedí na hranách displeje a jde od hlavičky ke spodní hraně (norma
    // §1b) — připnutí k výřezu dělá obal ve `Finance.jsx`, tady se jen vyplní.
    // Vlastní odsazení je **jedno** (16 px gutter); dřív se sčítalo odsazení
    // <main> s odsazením obsahu Financí na 32 px z každé strany a modul se tím
    // na 390px displeji zúžil o pětinu.
    //
    // Ovládání období a režimu **nescrolluje pryč** — je to navigace modulu,
    // ne obsah. Scrolluje jen blok pod ním a rezervu pro plovoucí lištu bere
    // dovnitř scrollu, aby obsah šel vytáhnout nad ni a modul přitom sahal až
    // ke spodní hraně.
    if (isMobile) {
        return (
            <div className="flex flex-col flex-1 min-h-0 min-w-0">
                <div className="flex-shrink-0 px-4 pt-3 pb-2">
                    {navigator}
                    <div className="mt-2 text-[11px] leading-snug" style={{ color: 'var(--text-secondary)' }}>
                        {vysvetlivka}
                        {loading && <Loader2 size={12} className="animate-spin inline-block ml-2" style={{ color: ACCENT }} />}
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-4"
                    style={{ paddingBottom: rezervaDole || 16, WebkitOverflowScrolling: 'touch' }}>
                    <div className="flex flex-col gap-3">
                        <VysledovkaVerdikt data={data} rezim={aktivniRezim} cardStyle={card} isDark={isDark} varianta="hero" />
                        <KDoreseni data={data} rezim={aktivniRezim} cardStyle={card} />
                        <PnlTabulka sekce={data.sekce} pacing={p} souhrn={data.souhrn}
                            cardStyle={card} isDark={isDark} rezim={aktivniRezim} isMobile />
                        <KpiKarty kpi={data.kpi} rezim={aktivniRezim} cardStyle={card} />
                        <StrukturaPruh segmenty={data.struktura?.[jeProj ? 'projekce' : 'zauctovano']} cardStyle={card} />
                    </div>
                </div>
            </div>
        );
    }

    // ── Desktop: dva sloupce, výšku drží stránka, scrollují sloupce ──────────
    return (
        <div className="flex flex-col xl:h-full xl:min-h-0">
            <div className="flex-shrink-0 mb-3">
                {navigator}
                <div className="mt-1.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                    {vysvetlivka}
                    {loading && <Loader2 size={12} className="animate-spin inline-block ml-2" style={{ color: ACCENT }} />}
                </div>
            </div>

            <div className="flex flex-col xl:flex-row gap-3 xl:flex-1 xl:min-h-0">
                <div className="flex flex-col flex-1 min-w-0 min-h-0">
                    <div className="flex-shrink-0 mb-2.5">
                        <VysledovkaVerdikt data={data} rezim={aktivniRezim} cardStyle={card} isDark={isDark} />
                    </div>
                    <PnlTabulka sekce={data.sekce} pacing={p} souhrn={data.souhrn}
                        cardStyle={card} isDark={isDark} rezim={aktivniRezim} />
                </div>

                {/* Třetí sloupec na normové šířce — `PANEL_SIRKA` 320 px od
                    `PANEL_OD` 1280 px (`components/layout/ModulLayout.jsx`, kde
                    jsou obě konstanty exportované právě proto, aby stránky se
                    stejným rozvržením seděly na pixel). `xl:` je v Tailwindu
                    týchž 1280 px. Dřív tu byl vlastní `clamp(340px,25vw,480px)`,
                    takže Výsledovka měla na širokém monitoru o 160 px jiný
                    sloupec než Doklady nebo Pokladna vedle. */}
                <div className="w-full xl:w-[320px] flex-shrink-0 min-h-0">
                    <VysledovkaStatistikyPanel data={data} rezim={aktivniRezim} cardStyle={card} isDark={isDark} />
                </div>
            </div>
        </div>
    );
}
